<template>
  <div>
    <div class="steps-header">
      <div class="steps-header-border">
        <div class="steps-header-layout">
          <page-step
            v-for="(step, index) in steps"
            :key="index"
            :step="step"
            :step-index="index"
            :current-step-index="currentStep.index"
            :is-changed="step.isChanged || false"
            :is-disabled="step.isDisabled"
            :previous-step-allowed="
              index === 0 ? true : !!nextButton[steps[index - 1].name]
            "
            @select-step="handleStepSelection"
          />
        </div>
      </div>
    </div>
    <div class="steps-content">
      <div
        class="steps-content-layout"
        :style="steps[currentStep.index].customTabStyles || {}"
      >
        <!--If keep alive-->
        <keep-alive v-if="keepAliveData">
          <rest-form
            v-if="model"
            ref="form"
            :rules="rules"
            :save-endpoint="saveEndpoint"
            :model="model"
            :validate-on-rule-change="false"
            :disabled="isReadOnly"
            :after-save="afterSave"
          >
            <component
              :is="steps[currentStep.index].component"
              :clicked-next="nextButton[currentStep.name]"
              @can-continue="proceed"
              @change-next="changeNextBtnValue"
              @complete-step="completeStep"
              @init="init"
              @calculate="
                samplingAllowed => this.$emit('calculate', samplingAllowed)
              "
              @check="$emit('check', $event)"
              @checkChanges="checkChanges"
              :current-step="currentStep"
              :steps="steps"
              :model="model"
              :recipients="recipients"
              :disabled="isReadOnly"
              :is-edit="isEdit"
              v-bind="steps[currentStep.index].bindProps || {}"
            />
          </rest-form>
        </keep-alive>
        <!--If not show component and destroy it in each step change-->
        <rest-form
          v-else-if="model"
          ref="form"
          :save-endpoint="saveEndpoint"
          :model="model"
          :validate-on-rule-change="false"
          :disabled="isReadOnly"
          :after-save="afterSave"
        >
          <component
            :is="steps[currentStep.index].component"
            :clicked-next="nextButton[currentStep.name]"
            @can-continue="proceed"
            @change-next="changeNextBtnValue"
            @complete-step="completeStep"
            @init="init"
            @calculate="
              samplingAllowed => this.$emit('calculate', samplingAllowed)
            "
            @check="$emit('check', $event)"
            @checkChanges="checkChanges"
            :current-step="currentStep"
            :steps="steps"
            :model="model"
            :recipients="recipients"
            :disabled="isReadOnly"
            :is-edit="isEdit"
            v-bind="steps[currentStep.index].bindProps || {}"
          />
        </rest-form>
      </div>
    </div>
  </div>
</template>
<script>
import RestForm from '@/common/components/rest/RestForm'
import PageStep from '@/common/components/page/PageStep'
import { EventBus } from '@/core/EventBus'
import { projectRoute, customConfirm } from '@/core/helpers'

export default {
  name: 'PageSteps',
  components: {
    RestForm,
    PageStep
  },
  props: {
    steps: {
      type: Array,
      required: false
    },
    keepAlive: {
      type: Boolean,
      default: true
    },
    reset: {
      type: Boolean,
      default: false
    },
    model: {
      type: Object,
      required: true
    },
    saveEndpoint: {
      type: Object,
      required: true
    },
    afterSave: {
      type: Function,
      required: false
    },
    rules: {
      type: Object,
      required: false
    },
    isEdit: {
      type: Boolean,
      default: false,
      required: false
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    validation: {
      type: Boolean,
      default: true
    },
    hash: {
      type: Boolean,
      default: false
    },
    recipients: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      currentStep: {},
      previousStep: {},
      nextButton: {},
      canContinue: false,
      finalStep: false,
      keepAliveData: this.keepAlive,
      hasChanges: false
    }
  },
  watch: {
    reset(val) {
      if (!val) {
        return
      }
      this.keepAliveData = false
      this.init()
      this.previousStep = {}
      this.$nextTick(() => {
        this.keepAliveData = this.keepAlive
        this.$emit('reset', true)
      })
    },
    finalStep(newValue) {
      this.$emit('final-step-updated', newValue)
    },
    canContinue(newValue) {
      this.$emit('can-continue-updated', newValue)
    },
    nextButton: {
      deep: true,
      handler() {
        this.$emit('next-button-updated', this.nextButton)
      }
    },
    currentStep: {
      deep: true,
      handler() {
        if (this.nextButton[this.currentStep.name]) {
          this.canContinue = true
        }
        EventBus.$emit('step-updated', this.steps[this.currentStep.index])

        if (this.hash) {
          // Add #hash to the end of URL
          const route = projectRoute({
            name: this.$route.name,
            hash: `#${this.currentStep.name}`,
            query: { ...this.$route.query }
          })
          if (!this.$route.hash) {
            this.$router.replace(route)
          } else if (this.$route.hash !== `#${this.currentStep.name}`) {
            this.$router.push(route)
          }
        }
      }
    },
    '$route.hash': {
      handler() {
        // Go to step from #hash
        if (
          this.hash &&
          this.$route.hash &&
          this.$route.hash.substring(1) !== this.currentStep.name
        ) {
          this.activateStepByHash()
        }
      }
    }
  },
  created() {
    if (!this.isEdit) {
      this.init()
    } else {
      this.initCompleted()
    }
  },

  mounted() {
    EventBus.$on('campaign-create-next-step', this.onCampaignNextBtnClick)
  },

  computed: {
    isReadOnly() {
      if (['analytics', 'preview'].includes(this.currentStep.name)) {
        return false
      }
      return this.readonly
    }
  },

  methods: {
    activateStep(index, back = false) {
      if (this.steps[index]) {
        this.previousStep = this.currentStep
        this.currentStep = {
          name: this.steps[index].name,
          index: index
        }
        if (index + 1 === this.steps.length) {
          this.finalStep = true
        } else {
          this.finalStep = false
        }
        if (!back) {
          this.$emit('completed-step', this.previousStep)
        }
        this.$emit('clear-errors')
      }
      EventBus.$emit('active-step', this.currentStep)
    },
    activateStepByHash() {
      const stepName = this.$route.hash.substring(1) // #hash -> hash
      if (stepName) {
        const index = this.steps
          .map(step => step.name)
          .findIndex(name => name === stepName)
        if (index >= 0 && !this.steps[index].isDisabled) {
          this.activateStep(index)
        } else {
          this.activateStep(0)
        }
      } else {
        this.activateStep(0)
      }
    },
    nextStepAction() {
      if (!this.validation) {
        this.$set(this.nextButton, this.currentStep.name, true)
      }
      if (this.canContinue) {
        if (this.finalStep) {
          this.$emit('stepper-finished', this.currentStep)
        }
        let currentIndex = this.currentStep.index + 1
        this.activateStep(currentIndex)
      }
      this.canContinue = false
      this.$forceUpdate()
    },
    nextStep() {
      if (!this.$listeners || !this.$listeners['before-next-step']) {
        this.nextStepAction()
      }
      this.canContinue = false
      this.$emit(
        'before-next-step',
        { currentStep: this.currentStep },
        (next = true) => {
          this.canContinue = true
          if (next) {
            this.nextStepAction()
          }
        }
      )
    },
    proceed(payload) {
      this.$set(this.steps[this.currentStep.index], 'completed', payload.value)
      this.canContinue = payload.value
    },
    changeNextBtnValue(payload) {
      this.$set(this.nextButton, this.currentStep.name, payload.nextBtnValue)
      this.$forceUpdate()
    },
    init() {
      // Initiate stepper
      if (this.hash && this.$route.hash) {
        this.activateStepByHash()
      } else {
        this.activateStep(0)
      }

      this.steps.forEach(step => {
        this.$set(this.nextButton, step.name, false)
        if (typeof step.completed !== 'boolean') {
          step.completed = false
        }
      })
    },
    initCompleted() {
      // Initiate stepper for edition
      this.activateStep(0)
      this.steps.forEach(step => {
        this.$set(this.nextButton, step.name, true)
        step.completed = true
      })
    },
    completeStep(index) {
      this.steps[index].completed = true
    },
    handleStepSelection(index) {
      if (!this.hasChanges) {
        this.selectStep(index)
        return
      }

      customConfirm(
        this.$t('Gamification_LeavingWithoutSaving'),
        this.$t('Gamification_LeavingWithoutSavingDescription'),
        this.$t('Gamification_Confirm_Button_Leave')
      ).then(() => {
        this.selectStep(index)
      })
    },
    selectStep(index) {
      if (this.validation) {
        if (this.currentStep.index !== index) {
          if (index === 0) {
            this.activateStep(index, true)
          } else if (this.nextButton[this.steps[index - 1].name]) {
            this.activateStep(index, true)
          } else if (index === this.currentStep.index + 1) {
            this.validate()
              .then(valid => {
                if (valid) {
                  this.completeStep(this.currentStep.index)
                  this.proceed({ value: true })
                  this.nextStep()
                }
              })
              .catch(error => {
                return Object.values(error).map(e => e[0].field)
              })
          } else if (index === this.currentStep.index - 1) {
            this.activateStep(index, true)
          } else if (
            this.steps.filter((step, i) => i < index && !step.completed)
              .length === 0
          ) {
            // if all steps preivous to the one we are clickinmg are complete, allow clicked step to be activated
            this.activateStep(index, true)
          }
        }
      } else {
        this.activateStep(index, true)
      }
    },
    save() {
      return this.$refs['form'].save()
    },
    validate() {
      return this.$refs['form'].validate()
    },
    onCampaignNextBtnClick() {
      this.activateStep(this.currentStep.index + 1)
      this.$nextTick(() => {
        const el = document.getElementById('page-header')
        if (el) {
          el.scrollIntoView(true)
        }
      })
    },
    checkChanges(payload) {
      this.hasChanges = payload
      this.$emit('checkChanges', payload)
    }
  }
}
</script>
<style scoped>
.steps-header {
  background: #f2f5f9;
  overflow-x: auto;
}
.steps-header-border {
  border-bottom: 1px solid #e5e5e5;
  padding: 0 22px;
}
.steps-header-layout {
  display: flex;
  margin: 0 auto;
  height: 40px;
}
.steps-content {
  background: #ffffff;
}
.steps-content-layout {
  padding: 22px;
  margin: 0 auto;
}
</style>



// WEBPACK FOOTER //
// src/common/components/page/PageSteps.vue